/* =========================================================================
   Home-page quiz answers on the product page

   "Quiz Behaviour & PDP Recommendation Logic", section 6: an answer given
   in a home-page quiz is remembered and shown on the product pages it is
   relevant to — and only those. Draws snippets/vro-pdp-memory-card.liquid:

     Your timepiece mood is Quiet Luxury
     Because you selected Quiet Luxury in the quiz, this will suit you
     perfectly.                                              [Retake]

   Where the answers come from (all on the personalization profile,
   assets/vro-personalization.js — localStorage for guests, the customer
   id and the server copy for signed-in shoppers):

     answer@<section>   a collection quiz: Watches, the health stack, and
                        any other quiz-teaser that answers with a
                        collection (assets/vro-quiz-memory.js). Relevant
                        when this product is in the answer's collection or
                        among its hand-picked products.
     quiz               "What are we sorting out?" (the four-step quiz).
                        Relevant when this product's own tags and data
                        answer the concern, the style lane or the occasion.

   Skin, hair and fragrance answers belong to the grooming card
   (assets/vro-pdp-match.js); sizes and body type to the size quiz
   (assets/vro-pdp-quiz.js). Neither is repeated here.

   The newest relevant answer wins; one card, never a list. Nothing
   relevant and the card stays hidden — the PDP is left as it was.

   Retake: a collection quiz asks its own question again in the card; the
   four-step quiz reopens in its modal. "Noted — you selected …" follows a
   Retake on this page view only.
   ========================================================================= */
(function () {
  'use strict';
  if (window.VroPdpMemory) return;

  var noted = null;          /* this page view only */
  var modalSince = null;     /* quizAt when the quiz modal was opened here */

  function engine() { return window.VroPersonalization || null; }
  function norm(v) {
    var e = engine();
    if (e && e.normalize) return e.normalize(v);
    return String(v || '').toLowerCase().replace(/[^a-z0-9%+]+/g, ' ').trim();
  }
  function slug(v) { return String(v || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
  function profileData() {
    var e = engine();
    if (!e || !e.profile) return null;
    try { return e.profile.get(); } catch (err) { return null; }
  }

  /* ---------------- What this product is ---------------- */

  function productOf(card) {
    return {
      handle: card.getAttribute('data-handle') || '',
      collections: card.getAttribute('data-collections') || '|',
      tags: card.getAttribute('data-tags') || '|',
      blob: norm((card.getAttribute('data-type') || '') + ' | ' + (card.getAttribute('data-blob') || '') + ' | ' +
        (card.getAttribute('data-tags') || '').split('|').join(' | '))
    };
  }
  function hasTag(prod, tag) { return prod.tags.indexOf('|' + String(tag).toLowerCase() + '|') > -1; }
  function inCollection(prod, handle) { return !!handle && prod.collections.indexOf('|' + handle + '|') > -1; }
  function says(prod, phrases) {
    for (var i = 0; i < phrases.length; i++) {
      var p = norm(phrases[i]);
      if (p && (' ' + prod.blob + ' ').indexOf(' ' + p) > -1) return true;
    }
    return false;
  }

  /* ---------------- Collection quizzes (answer@…) ---------------- */

  function isGroomingAnswer(v) {
    return /skin|scalp|hair|perfume|scent|fragrance/i.test(String(v.q || '') + ' ' + String(v.s || ''));
  }

  /* "Which style is more you?" and the like: they tune the home page's
     racks and are never carried to a product page. */
  function isStyleAnswer(v) {
    var text = String(v.q || '') + ' ' + String(v.s || '');
    if (/timepiece|watch|stack|supplement|nutrition|health/i.test(text)) return false;
    return /style|wear|outfit|look/i.test(text);
  }

  function headingFor(v) {
    if (v.h) return v.h;
    var text = String(v.q || '') + ' ' + String(v.s || '');
    if (/timepiece|watch/i.test(text)) return 'Your timepiece mood is [answer]';
    if (/stack|supplement|nutrition|health/i.test(text)) return 'Your health stack needs [answer]';
    if (/style|wear|outfit|look|fit/i.test(text)) return 'Your style is [answer]';
    return 'Your pick is [answer]';
  }

  function answerFits(prod, v) {
    /* The answer's own shelf: its hand-picked products and its collection. */
    if (v.p && v.p.indexOf(prod.handle) > -1) return true;
    if (v.c && inCollection(prod, v.c)) return true;
    /* The collection named after the answer, for an answer filed before
       the collection was recorded ("Omega-3" -> omega-3, collagen-1). */
    var s = slug(v.a);
    if (!v.c && s && (inCollection(prod, s) || new RegExp('\\|' + s + '-\\d+\\|').test(prod.collections))) return true;
    /* The product saying it in its own structured data: a tag that is the
       answer ("Quiet Luxury" on a Titan), or its type, tags, benefits or
       ingredients naming it ("Creatine & BCAA"). Never the title. */
    if (hasTag(prod, v.a)) return true;
    return says(prod, [v.a]);
  }

  /* ---------------- "What are we sorting out?" ---------------- */

  /* How the sheet's answers read on the catalogue's own tags. */
  var FASHION_UPGRADE_TAGS = {
    'everyday wear': ['everyday', 'casual'],
    'smartwear': ['work', 'at office', 'formal', 'smart casual'],
    'party fits': ['party', 'at parties'],
    'weekend fits': ['weekend'],
    'statement looks': ['statement', 'party', 'street']
  };

  function quizCandidates(prod, quiz) {
    var e = engine();
    if (!quiz || !quiz.category || !e) return [];
    var cat = (e.quiz && e.quiz.keyFor) ? e.quiz.keyFor(quiz.category) : quiz.category;
    var signals = e.problemSignals || {};
    var problems = quiz.problems || [];
    var out = [];

    function problemFits(pr) {
      var list = signals[norm(pr)] || [pr];
      return says(prod, list);
    }

    if (cat === 'Health & Energy') {
      if (!hasTag(prod, 'health & nutrition') && !inCollection(prod, 'health-nutrition')) return [];
      problems.forEach(function (pr) {
        if (problemFits(pr)) out.push({ heading: 'Your focus is [answer]', answer: pr });
      });
    } else if (cat === 'Fashion & Styling') {
      /* Style answers stay on the home page; apparel PDPs show size only. */
      return [];
    } else if (cat === 'Lifestyle') {
      /* The gear they picked (watches, footwear, bags) decides whether this
         product is in the answer at all; the occasion and the style lane
         are what the card can honestly say about it. */
      var gear = problems.some(problemFits);
      if (!gear) return [];
      if (quiz.condition && hasTag(prod, quiz.condition)) out.push({ heading: 'Your occasion is [answer]', answer: quiz.condition });
      if (quiz.trait && hasTag(prod, quiz.trait)) out.push({ heading: 'Your style lane is [answer]', answer: quiz.trait });
    } else if (cat === 'Technology') {
      problems.forEach(function (pr) {
        if (problemFits(pr)) out.push({ heading: 'Sorted for your [answer]', answer: pr });
      });
    }
    /* Skin, Hair & Scalp and Fragrance: the grooming card's. */
    return out;
  }

  /* ---------------- Choosing what to show ---------------- */

  function best(prod) {
    var p = profileData();
    if (!p) return null;
    var facets = p.facets || {};
    var at = p.facetAt || {};
    var found = [];

    Object.keys(facets).forEach(function (k) {
      if (k.indexOf('answer@') !== 0) return;
      var v = facets[k];
      if (!v || typeof v !== 'object' || !v.a || isGroomingAnswer(v) || isStyleAnswer(v)) return;
      if (!answerFits(prod, v)) return;
      found.push({ at: at[k] || 0, heading: headingFor(v), answer: v.a, retake: { kind: 'options', key: k, entry: v } });
    });

    var qc = quizCandidates(prod, p.quiz);
    if (qc.length) {
      var c = qc[0];
      found.push({ at: p.quizAt || 0, heading: c.heading, answer: c.answer, retake: { kind: 'modal' } });
    }

    found.sort(function (a, b) { return b.at - a.at; });
    return found[0] || null;
  }

  /* ---------------- Drawing ---------------- */

  function paint(card) {
    var prod = productOf(card);
    var title = card.querySelector('[data-memory-title]');
    var text = card.querySelector('[data-memory-text]');
    var retake = card.querySelector('[data-memory-retake]');
    var asking = card.classList.contains('is-asking');

    if (noted) {
      title.textContent = 'Noted — you selected ' + noted;
      text.textContent = '';
      text.hidden = true;
      if (retake) retake.hidden = asking;
      card.hidden = false;
      return;
    }

    var hit = best(prod);
    card.__vroMemory = hit;
    if (!hit) {
      if (!asking) card.hidden = true;
      return;
    }
    /* "Your timepiece mood is <b>Quiet Luxury</b>" — the answer in bold,
       as the size card has it ("Recommended Size is <b>UK 8</b>"). */
    var esc = function (v) {
      return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    };
    title.innerHTML = hit.heading.split('[answer]').map(esc).join('<b>' + esc(hit.answer) + '</b>');
    text.textContent = 'Because you picked ' + hit.answer + ' in the quiz, this should suit you well.';
    text.hidden = false;
    if (retake) retake.hidden = asking;
    card.hidden = false;
  }

  function run() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-pdp-memory]'), paint);
  }

  /* ---------------- Retake ---------------- */

  function openOptions(card, hit) {
    var entry = hit.retake.entry;
    var box = card.querySelector('[data-memory-options]');
    var chips = card.querySelector('[data-memory-chips]');
    var q = card.querySelector('[data-memory-question]');
    if (!box || !chips || !entry.o || !entry.o.length) return false;
    q.textContent = entry.s || entry.q || '';
    chips.innerHTML = '';
    entry.o.forEach(function (o) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'pdp-chip';
      b.setAttribute('data-memory-choice', o.a);
      b.setAttribute('aria-pressed', o.a === entry.a ? 'true' : 'false');
      var span = document.createElement('span');
      span.textContent = o.a;
      b.appendChild(span);
      chips.appendChild(b);
    });
    card.__vroAsking = hit;
    card.classList.add('is-asking');
    box.hidden = false;
    return true;
  }

  function openModal() {
    var p = profileData();
    modalSince = (p && p.quizAt) || 0;
    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.hidden = true;
    trigger.setAttribute('data-quiz-open', '');
    document.body.appendChild(trigger);
    trigger.click();
    setTimeout(function () { trigger.remove(); }, 0);
  }

  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target : null;
    if (!t) return;
    var card = t.closest('[data-pdp-memory]');
    if (!card) return;

    if (t.closest('[data-memory-retake]')) {
      e.preventDefault();
      var hit = card.__vroMemory;
      if (!hit) return;
      if (hit.retake.kind === 'options' && openOptions(card, hit)) {
        t.closest('[data-memory-retake]').hidden = true;
      } else {
        openModal();
      }
      return;
    }

    var choice = t.closest('[data-memory-choice]');
    if (choice && card.__vroAsking) {
      e.preventDefault();
      var asked = card.__vroAsking;
      var label = choice.getAttribute('data-memory-choice');
      var entry = JSON.parse(JSON.stringify(asked.retake.entry));
      var picked = null;
      (entry.o || []).forEach(function (o) { if (o.a === label) picked = o; });
      entry.a = label;
      entry.c = (picked && picked.c) || '';
      /* The hand-picked products were the old answer's. This one brings its
         own where they were filed with the options (vro-quiz-memory.js);
         otherwise it is known by its collection until it is answered on
         the home page again. */
      if (picked && picked.p && picked.p.length) entry.p = picked.p.slice();
      else delete entry.p;
      card.classList.remove('is-asking');
      card.__vroAsking = null;
      var box = card.querySelector('[data-memory-options]');
      if (box) box.hidden = true;
      noted = label;
      var eng = engine();
      if (eng && eng.profile && eng.profile.setFacet) eng.profile.setFacet(asked.retake.key, entry);
      run();
    }
  });

  /* The four-step quiz, retaken in its modal on this page, is saved by the
     modal; the first answer that lands after it opened is the Noted one. */
  function onProfileChange() {
    if (modalSince !== null) {
      var p = profileData();
      if (p && p.quiz && (p.quizAt || 0) > modalSince) {
        var bits = [];
        if (p.quiz.problems && p.quiz.problems.length) bits.push(p.quiz.problems.join(', '));
        if (p.quiz.condition) bits.push(p.quiz.condition);
        if (bits.length) noted = bits.join(' & ');
        modalSince = null;
      }
    }
    run();
  }

  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    noted = null;
    run();
  });

  window.VroPdpMemory = { run: run, best: function (card) { return best(productOf(card)); } };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  window.addEventListener('load', run);
  document.addEventListener('vro:personalization:ready', run);
  document.addEventListener('vro:profile:change', onProfileChange);
})();
